/**
 * analytics.ts
 *
 * Single source of truth for all analytics tracking.
 *
 * CRITICAL FIX: device_id is now written to every app_visits insert.
 * Previously this was missing, causing MAU/DAU/NEW/RETURNING to show 0.
 *
 * device_id is a random UUID stored in localStorage as 'hhu_device_id'.
 * It persists across sessions and is the basis for returning user detection.
 * It is NOT linked to any personal information.
 */

import { supabase } from '../lib/supabase'

// ─────────────────────────────────────────────
// DEVICE ID — persistent anonymous identifier
// ─────────────────────────────────────────────

function getOrCreateDeviceId(): string {
  try {
    let deviceId = localStorage.getItem('hhu_device_id')
    if (!deviceId) {
      deviceId = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36)
      localStorage.setItem('hhu_device_id', deviceId)
    }
    return deviceId
  } catch {
    // localStorage unavailable (private browsing edge case)
    return 'unknown'
  }
}

// Initialise device_id immediately on module load so it exists
// before any trackVisit call fires
export const DEVICE_ID = getOrCreateDeviceId()

// ─────────────────────────────────────────────
// SESSION ID — resets on every browser/app restart
// ─────────────────────────────────────────────

function getOrCreateSessionId(): string {
  try {
    let sid = sessionStorage.getItem('hhu_session_id')
    if (!sid) {
      sid = Math.random().toString(36).slice(2) + Date.now().toString(36)
      sessionStorage.setItem('hhu_session_id', sid)
    }
    return sid
  } catch {
    return 'unknown'
  }
}

/**
 * Start a fresh analytics session. Called by AppContext when the app
 * resumes after a >30 min background gap. In native apps the JS process
 * (and sessionStorage) can survive for days, so without rotation a user's
 * Tuesday and Thursday usage counted as ONE session and their revisits
 * were invisible — systematically undercounting your most loyal users.
 */
export function rotateAnalyticsSession(): void {
  try { sessionStorage.removeItem('hhu_session_id') } catch { }
  _trackedPages.clear()
  _trackedSearches.clear()
}

// ─────────────────────────────────────────────
// VENUE STAT TRACKER
// ─────────────────────────────────────────────

async function trackVenueStat(venueId: string, stat: string) {
  try {
    await supabase.rpc('increment_venue_stat', { p_venue_id: venueId, p_stat: stat })
  } catch { }
}

// ─────────────────────────────────────────────
// AD STAT TRACKER
// ─────────────────────────────────────────────

async function trackAdEvent(adId: string, brandName: string, eventType: 'impression' | 'click') {
  // Server-side write via /api/track-ad — NOT a direct Supabase insert.
  // This lets the anon INSERT policy on ad_events be revoked, so metrics
  // that back brand invoices can't be fabricated with the public anon key.
  // keepalive lets click events survive the immediate navigation away.
  try {
    await fetch('/api/track-ad', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ad_id: adId, brand_name: brandName, event_type: eventType }),
      keepalive: true,
    })
  } catch { }
}

// ─────────────────────────────────────────────
// SEARCH TRACKER — writes to search_events
// Moved here from deviceAnalytics.ts (now deleted) so all
// Supabase analytics writes live in one service.
// ─────────────────────────────────────────────

// Dedup identical queries within a session so one user typing the same
// thing repeatedly doesn't inflate search counts.
const _trackedSearches = new Set<string>()

async function trackSearchEvent(query: string, resultCount: number) {
  const q = query.trim().toLowerCase()
  if (!q || q.length < 2) return
  if (_trackedSearches.has(q)) return
  _trackedSearches.add(q)
  try {
    await supabase.from('search_events').insert([{
      device_id: DEVICE_ID,
      query:     q,
      results:   resultCount,
    }])
  } catch { }
}

// ─────────────────────────────────────────────
// VISIT TRACKER — core analytics write
// ─────────────────────────────────────────────

// Module-level Set survives React remounts — sessionStorage does not.
// This is the reliable dedup gate that prevents double-counting on re-renders.
const _trackedPages = new Set<string>()

async function trackVisit(page = 'browse') {
  try {
    // Gate: only track once per JS runtime per page
    // Module-level Set persists across component mounts/unmounts
    if (_trackedPages.has(page)) return
    _trackedPages.add(page)

    const sessionId = getOrCreateSessionId()
    const deviceId  = DEVICE_ID

    await supabase.from('app_visits').insert([{
      session_id: sessionId,
      device_id:  deviceId,
      page,
      created_at: new Date().toISOString(),
    }])
  } catch { }
}

// ─────────────────────────────────────────────
// PROVIDER INTERFACE
// ─────────────────────────────────────────────
// PostHog was previously wired here as an alternate provider, gated on
// `window.posthog` existing. It never did: no init script ever set that
// global, and posthog-js was never even a declared dependency (present
// in node_modules from a one-off `npm install` that was never wired up
// or committed to package.json). Every track() call silently took this
// same consoleProvider path the whole time — this just makes that the
// only path, instead of dead code pretending there was a second one.

interface AnalyticsProvider {
  init(): void
  track(event: string, props?: Record<string, unknown>): void
  identify(userId: string, traits?: Record<string, unknown>): void
  page(pageName: string, props?: Record<string, unknown>): void
}

const provider: AnalyticsProvider = {
  init: () => {},
  track: (event, props) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics]', event, props)
    }
  },
  identify: () => {},
  page: () => {},
}

// ─────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────

export function initAnalytics() { provider.init() }
export function track(event: string, props?: Record<string, unknown>) { provider.track(event, props) }
export function identifyUser(userId: string, traits?: Record<string, unknown>) { provider.identify(userId, traits) }
export function trackPage(pageName: string, props?: Record<string, unknown>) { provider.page(pageName, props) }

// ─────────────────────────────────────────────
// TYPED EVENT HELPERS
// ─────────────────────────────────────────────

export const Analytics = {
  appOpened: () => track('app_opened'),

  // Fired by AppContext after a resume-with-session-rotation, so the
  // fresh session registers a visit even if the user never navigates.
  appResumed: () => {
    trackVisit('resume')
    track('app_resumed')
  },

  filterUsed: (key: string, value: string | boolean) =>
    track('filter_used', { filter_key: key, filter_value: value }),

  happeningNowToggled: (enabled: boolean) =>
    track('happening_now_toggled', { enabled }),

  venueCardClicked: (venueId: string, venueName: string, isOpen: boolean) => {
    track('venue_card_clicked', { venue_id: venueId, venue_name: venueName, is_open: isOpen })
    trackVenueStat(venueId, 'card_views')
  },

  mapPinClicked: (venueId: string, venueName: string) =>
    track('map_pin_clicked', { venue_id: venueId, venue_name: venueName }),

  favoriteAdded: (venueId: string, venueName: string) => {
    track('favorite_added', { venue_id: venueId, venue_name: venueName })
    trackVenueStat(venueId, 'favorites')
  },

  favoriteRemoved: (venueId: string, venueName: string) =>
    track('favorite_removed', { venue_id: venueId, venue_name: venueName }),

  venueDetailViewed: (venueId: string, venueName: string) => {
    track('venue_detail_viewed', { venue_id: venueId, venue_name: venueName })
  },

  submissionStarted: (flow: 'new_venue' | 'suggest_edit') =>
    track('submission_started', { flow }),

  submissionCompleted: (flow: 'new_venue' | 'suggest_edit', venueId?: string) =>
    track('submission_completed', { flow, venue_id: venueId }),

  outboundWebsiteClicked: (venueId: string, url: string) => {
    track('outbound_website_clicked', { venue_id: venueId, url })
    trackVenueStat(venueId, 'website_clicks')
  },

  getDirectionsClicked: (venueId: string, venueName: string) => {
    track('get_directions_clicked', { venue_id: venueId, venue_name: venueName })
    trackVenueStat(venueId, 'directions_clicks')
  },

  phoneClicked: (venueId: string, venueName: string) => {
    track('phone_clicked', { venue_id: venueId, venue_name: venueName })
    trackVenueStat(venueId, 'phone_clicks')
  },

  viewModeChanged: (mode: 'list' | 'map' | 'split') =>
    track('view_mode_changed', { mode }),

  bestPicksSectionViewed: (sectionId: string) =>
    track('best_picks_section_viewed', { section_id: sectionId }),

  seoPageViewed: (pageType: string, city: string, neighborhood?: string) =>
    track('seo_page_viewed', { page_type: pageType, city, neighborhood }),

  searchPerformed: (query: string, resultCount: number) => {
    track('search_performed', { query, result_count: resultCount })
    trackSearchEvent(query, resultCount)
  },

  adImpression: (adId: string, brandName: string) => {
    track('ad_impression', { ad_id: adId, brand_name: brandName })
    trackAdEvent(adId, brandName, 'impression')
  },

  adClicked: (adId: string, brandName: string) => {
    track('ad_clicked', { ad_id: adId, brand_name: brandName })
    trackAdEvent(adId, brandName, 'click')
  },

  pageVisited: (page: string) => {
    trackVisit(page)
    track('page_visited', { page })
  },

  emailSignup: (email: string, source: string) =>
    track('email_signup', { email, source }),
}
